import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { useRef, useState } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { NativeProps } from '../utils/native-props';
import './style.less';

export type IndexBarOptionKey = string | number;

export type IndexBarOption = {
  key: IndexBarOptionKey;
  name: string;
};

export type IndexBarProps = {
  onChange: (key) => void;
  options: IndexBarOption[];
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  activeKey?: IndexBarOptionKey;
} & NativeProps;

export const IndexBar = (props: IndexBarProps) => {
  const classPrefix = 'v-index-bar';
  const [activeKey, setActiveKey] = useState<IndexBarOptionKey>('');
  const forceUpdate = hooks.useForceUpdate();
  const interacting = useRef(false);

  useEffectCustom(() => {
    if (props.activeKey) {
      setActiveKey(props.activeKey);
    }
  }, [props.activeKey]);

  const onChange = hooks.useCallbackRef((key: IndexBarOptionKey) => {
    if (key !== activeKey) {
      setActiveKey(key);
      props.onChange(key);
    }
  });

  useEffectCustom(() => {
    if (interacting.current) {
      props.onTouchStart?.();
    } else {
      props.onTouchEnd?.();
    }
  }, [interacting.current]);

  const onTouchMove = hooks.useThrottleCallback((e) => {
    // e.preventDefault();
    if (!interacting) return;
    const { clientX, clientY } = e.touches[0];
    const target = document.elementFromPoint(clientX as number, clientY as number) as HTMLElement;
    if (!target) return;
    const index = target.dataset['index'];
    if (index) {
      onChange(index);
    }
  });
  return (
    <div
      className={classPrefix}
      onTouchMove={onTouchMove}
      onMouseDown={() => {
        interacting.current = true;
      }}
      onMouseUp={() => {
        interacting.current = false;
        forceUpdate();
      }}
      onTouchStart={() => {
        interacting.current = true;
      }}
      onTouchEnd={() => {
        interacting.current = false;
        forceUpdate();
      }}
    >
      {props.options.map((item) => {
        const isActive = activeKey === item.key;
        return (
          <div
            className={`${classPrefix}-item`}
            key={item.key}
            onMouseDown={() => {
              onChange(item.key);
            }}
            onTouchStart={() => {
              onChange(item.key);
            }}
            onMouseEnter={() => {
              if (interacting.current) {
                onChange(item.key);
              }
            }}
          >
            <div
              className={classNames(`${classPrefix}-item-content`, {
                [`${classPrefix}-item-active`]: isActive,
              })}
              data-index={item.key}
            >
              {item.name}
              {interacting.current && isActive && (
                <div className={`${classPrefix}-item-bubble`}>{item.name}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
